import { FormSchema, SchemaFormGroupProps } from "@sw-sw/lib-form";
import { UIControlType } from "@sw-sw/lib-form-control-types";
import { camelCase } from "lodash";
import React, { PropsWithChildren, useState } from "react";
import { useLocation } from "react-router-dom";
import divisionApi, { CreateApi, DeleteApi } from "../utils/api/division";

export interface IDivisionProviderProps extends PropsWithChildren<{}> {}

export interface IDivisionContextProps {
  disableSwitcher: boolean;
  setDisableSwitcher: Function;
  selectedDivision: Division | null;

  selectDivision: (id: number) => Promise<any>;
  tabs: { [key: string]: DivisionResource };
  /** Key of current tab. See `tabs` option. Resource name.*/
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
  /** List of resource names. */
  resources: Array<string>;
  resetSelectedTab: () => void;
}

export interface Division {
  id: number;
  name: string;
  archived_at?: string | null;
}

const schemaTmpl: Partial<SchemaFormGroupProps> = {
  controlType: UIControlType.customTagSelect,
  labelKey: "name",
  valueKey: "id",
  isMulti: true,
};

type DivisionResource = {
  divisionApi: {
    create: CreateApi;
    delete: DeleteApi;
  };
  header: string;
  tableHeaders: [string, string][];
  callToActionContent: string;
  detailRoute?: (resourceObj: any) => string;
  formSchema: FormSchema;
  disablePagination?: true;
};

const tabs: {
  [key: string]: DivisionResource;
} = {
  regulations: {
    divisionApi: divisionApi.regulations,
    header: "Regulations",
    tableHeaders: [["Name", "name"]],
    callToActionContent: "Regulations",
    detailRoute: reg => `/groups/${reg.id}`,
    formSchema: {
      regulations: {
        ...schemaTmpl,
        label: "Regulations",
        noOptionMessage: "No regulations found",
        placeholder: "Search for regulations",
        validation: {
          required: true,
        },
      },
    },
  },
  clients: {
    divisionApi: divisionApi.clients,
    header: "Clients",

    tableHeaders: [["Name", "name"]],
    callToActionContent: "Clients",
    detailRoute: client => `/clients/${client.id}`,
    formSchema: {
      clients: {
        ...schemaTmpl,
        label: "Clients",
        noOptionMessage: "No clients found",
        placeholder: "Search for clients",
        validation: {
          required: true,
        },
      },
    },
  },
  users: {
    divisionApi: divisionApi.users,
    header: "Users",

    tableHeaders: [
      ["First Name", "first_name"],
      ["Last Name", "last_name"],
      ["Email", "email"],
      ["Role", "roleName"],
    ],
    callToActionContent: "Users",
    detailRoute: user => `/admin/?search=${user.email}`,
    formSchema: {
      users: {
        ...schemaTmpl,
        label: "Users",
        noOptionMessage: "No users found",
        placeholder: "Search for users",
        validation: {
          required: true,
        },
      },
    },
  },
  inspectionIntervals: {
    divisionApi: divisionApi.inspectionIntervals,
    header: "Inspection Intervals",

    tableHeaders: [
      ["Name", "name"],
      ["Interval", "interval"],
    ],
    callToActionContent: "Interval",
    detailRoute: () => `/administration/settings`,
    formSchema: {
      inspectionIntervals: {
        ...schemaTmpl,
        label: "Inspection Intervals",
        noOptionMessage: "No Inspection Intervals found",
        placeholder: "Search for Inspection Intervals",
        validation: {
          required: true,
        },
      },
    },
  },
  billingAddons: {
    divisionApi: {
      create: (() => Promise.resolve([])) as unknown as CreateApi,
      delete: (() => Promise.resolve([])) as unknown as DeleteApi,
    },
    header: 'Billing Add-ons',
    tableHeaders: [] as [string, string][],
    callToActionContent: '',
    formSchema: {},
  },
  areas: {
    divisionApi: divisionApi.areas,
    header: "Areas",

    tableHeaders: [["Name", "name"], ["Created Date", "createdAt"]],
    callToActionContent: "Areas",
    detailRoute: area => `/areas/${area.id}`,
    formSchema: {
      areas: {
        ...schemaTmpl,
        label: "Areas",
        noOptionMessage: "No Areas found",
        placeholder: "Search for Areas",
        validation: {
          required: true,
        },
      },
    },
  },
  documentGroups: {
    divisionApi: divisionApi.documentTypeGroups,
    header: "Document Groups",

    tableHeaders: [["Name", "name"]],
    callToActionContent: "Document Groups",
    formSchema: {
      documentGroups: {
        ...schemaTmpl,
        label: "Document Groups",
        noOptionMessage: "No Document Groups found",
        placeholder: "Search for Document Groups",
        validation: {
          required: true,
        },
      },
    },
  }
};

const Context = React.createContext<IDivisionContextProps>(undefined!);

export const DivisionProvider: React.FC<IDivisionProviderProps> = ({
  children,
}) => {
  const location = useLocation();
  const [selectedDivision, setSelectedDivision] =
    useState<Division | null>(null);
  const resources = Object.keys(tabs);
  const [selectedTab, setSelectedTab] = useState(
    getInitialSelectedTab({ resources, pathname: location.pathname }),
  );
  const [disableSwitcher, setDisableSwitcher] = useState<boolean>(false);

  /** @todo refactor into hook */
  const selectDivision = async (id: number) => {
    const division = await divisionApi.get(id);

    setSelectedDivision(division);
  };

  const resetSelectedTab = () => {
    setSelectedTab(
      getInitialSelectedTab({ resources, pathname: location.pathname }),
    );
  };

  return (
    <Context.Provider
      value={{
        disableSwitcher,
        setDisableSwitcher,
        tabs,
        selectedTab,
        setSelectedTab,
        selectedDivision,
        selectDivision,
        resources,
        resetSelectedTab,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default Context;
export const DivisionContext = Context;

function getInitialSelectedTab({
  resources,
  pathname,
}: {
  resources: string[];
  pathname: string;
}) {
  const [path] = pathname.split("/").slice(-1);
  const resourceName = camelCase(path);

  if (resources.includes(resourceName)) {
    return resourceName;
  } else {
    return resources[0];
  }
}
